import React from 'react';
import { Link } from 'react-router-dom';
import CompanyLegal from '../components/CompanyLegal';

const EFFECTIVE_DATE = 'March 13, 2026';

const SECTIONS = [
  {
    id: 'scope', num: '1', title: 'Scope and purpose',
    body: (
      <p>
        This Privacy Policy describes how Moneli Automation Inc. ("Moneli", "we", "our") handles
        Customer Data and personal information in our solutions and services. It explains how information
        is collected, stored, processed, disclosed, transferred, protected, retained, and destroyed.
      </p>
    ),
  },
  {
    id: 'collect', num: '2', title: 'Information we collect',
    body: (
      <ul>
        <li><strong>Customer-provided data:</strong> data uploaded or entered by customer users, including business content and personal information included in workflows.</li>
        <li><strong>Account and contact data:</strong> names, emails, organization details, and service communication records.</li>
        <li><strong>Technical and usage data:</strong> logs, timestamps, IP/device metadata, audit events, and operational telemetry for security and reliability.</li>
      </ul>
    ),
  },
  {
    id: 'how-collected', num: '3', title: 'How information is collected',
    body: (
      <ul>
        <li>Directly from customers and authorized users via forms, uploads, integrations, and communications.</li>
        <li>Automatically through service usage, authentication events, and security monitoring.</li>
        <li>From integrated systems as instructed and authorized by the customer.</li>
      </ul>
    ),
  },
  {
    id: 'use', num: '4', title: 'How we use and process information',
    body: (
      <ul>
        <li>To provide, operate, maintain, and improve contracted services.</li>
        <li>To perform workflows, analytics, automation, reporting, and support functions requested by the customer.</li>
        <li>To secure systems, detect abuse, investigate incidents, and meet legal obligations.</li>
      </ul>
    ),
  },
  {
    id: 'storage', num: '5', title: 'Storage and hosting',
    body: (
      <ul>
        <li>Customer Data is stored in controlled infrastructure with access controls, encryption, and monitoring.</li>
        <li>Data location/hosting architecture is defined per engagement and may include cloud providers approved by the customer.</li>
        <li>We implement environment separation (e.g., production vs. non-production) where applicable.</li>
      </ul>
    ),
  },
  {
    id: 'sharing', num: '6', title: 'Disclosure and sharing',
    body: (
      <ul>
        <li>We do not sell Customer personal information.</li>
        <li>We share data only with authorized subprocessors/service providers required to deliver the service, under contractual safeguards.</li>
        <li>We may disclose data when legally required or to protect rights, safety, and service integrity.</li>
      </ul>
    ),
  },
  {
    id: 'transfer', num: '7', title: 'International transfer',
    body: (
      <p>
        Where data transfers occur across jurisdictions, Moneli applies appropriate contractual and organizational
        safeguards consistent with applicable privacy laws and customer agreements.
      </p>
    ),
  },
  {
    id: 'security', num: '8', title: 'Security protections',
    body: (
      <ul>
        <li>Role-based access controls and least-privilege practices.</li>
        <li>Encryption in transit and at rest where supported by platform components.</li>
        <li>Audit logging and operational monitoring.</li>
        <li>Change controls, secure development practices, and periodic security updates.</li>
      </ul>
    ),
  },
  {
    id: 'retention', num: '9', title: 'Retention and destruction',
    body: (
      <ul>
        <li>We retain data only as long as needed for service delivery, legal obligations, and documented customer requirements.</li>
        <li>Upon termination or request (subject to agreement/law), data is deleted or returned according to contractual terms.</li>
        <li>Deletion methods are designed to prevent unauthorized recovery in line with platform capabilities.</li>
      </ul>
    ),
  },
  {
    id: 'rights', num: '10', title: 'Customer rights and controls',
    body: (
      <p>
        Customers may request access, correction, export, or deletion of personal information as permitted by law
        and contract. Requests can be submitted through the contact channel below.
      </p>
    ),
  },
  {
    id: 'related', num: '11', title: 'Related documents',
    body: (
      <ul>
        <li>Customer contract / Statement of Work (SOW)</li>
        <li>Data Processing Addendum (DPA), where applicable</li>
        <li>Subprocessor and security documentation (available upon request/contractual process)</li>
      </ul>
    ),
  },
  {
    id: 'contact', num: '12', title: 'Contact',
    body: (
      <div className="co-prose__contact">
        <p>
          For privacy and data-handling inquiries, contact Moneli Automation via{' '}
          <Link to="/contact">our contact page</Link>.
        </p>
      </div>
    ),
  },
];

function PrivacyPolicy() {
  return (
    <CompanyLegal
      slug="privacy"
      crumb="Privacy Policy"
      title={<>Privacy <em>policy</em></>}
      lead="How Moneli Automation collects, stores, processes, protects, retains and destroys Customer Data and personal information."
      meta={[{ label: 'Effective date', value: EFFECTIVE_DATE }]}
      sections={SECTIONS}
    />
  );
}

export default PrivacyPolicy;
